import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { CmsSearchBoxComponent, Product, RoutingService, SearchboxService, WindowRef } from '@spartacus/core';
import {
  BREAKPOINT,
  BreakpointService,
  CmsComponentData,
  ICON_TYPE,
  SearchBoxComponentService,
  SearchBoxConfig,
  SearchResults,
} from '@spartacus/storefront';
import { Observable, Subscription, combineLatest, fromEvent, of, timer } from 'rxjs';
import { auditTime, debounceTime, delayWhen, map, retryWhen, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { customDeviceStylings } from 'src/app/service/custom-device-stylings/custom-device-stylings.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerProductListName,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';

const DEFAULT_SEARCHBOX_CONFIG: SearchBoxConfig = {
  minCharactersBeforeRequest: 1,
  displayProducts: true,
  displaySuggestions: true,
  maxProducts: 5,
  maxSuggestions: 5,
  displayProductImages: true,
};

@Component({
  selector: 'app-search-box',
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SearchBoxComponent implements OnInit, OnDestroy {
  config: SearchBoxConfig;
  resultsSubscription: Subscription;
  businessType: string;
  /**
   * Sets the search box input field
   */
  @Input('queryText')
  set queryText(value: string) {
    if (value) {
      this.search(value);
    }
  }

  iconTypes = ICON_TYPE;

  /**
   * In some occasions we need to ignore the close event,
   * for example when we click inside the search result section.
   */
  private ignoreCloseEvent = false;
  placeholder: any;
  /**
   * The component data is optional, so that this component
   * can be reused without CMS integration.
   */

  gtmBusinessType: string = this.currentBusinesstype.getBusinessType();
  affiliation = `/${this.gtmBusinessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;

  cxMobileWidth = this.breakpointService.getSize(BREAKPOINT.sm);
  showSrp: boolean;
  adjustedTabletStylings = false;
  srpSubscription$: Subscription;
  basepriceBehaviorConfig$: Subscription;
  basepriceHidden = false;
  runSearch = false;
  @ViewChild('searchInput', { static: false }) searchInput: any;

  constructor(
    protected searchBoxComponentService: SearchBoxComponentService,
    protected router: Router,
    protected componentService: ComponentService,
    private routingService: RoutingService,
    protected searchService: SearchboxService,
    @Optional()
    protected componentData: CmsComponentData<CmsSearchBoxComponent>,
    protected winRef: WindowRef,
    private countryService: ShimanoCountryService,
    private languageService: ShimanoLanguageService,
    private breakpointService: BreakpointService,
    private cdr: ChangeDetectorRef,
    private gtmService: GoogleTagManagerService,
    private currentBusinesstype: CurrentBusinessType,
    private storageService: StorageService,
    private dealerPriceService: DealerPriceService,
    private deviceStylings: customDeviceStylings
  ) {
    this.tabletCustomSylings();
  }

  results$: Observable<SearchResults> = this.config$.pipe(
    debounceTime(500),
    tap((c) => (this.config = c)),
    switchMap((config) => this.searchBoxComponentService.getResults(config))
  );

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  ngOnInit() {
    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.basepriceBehaviorConfig$ = this.dealerPriceService.getBasepriceBehavior$().subscribe((hideBaseprice: any) => {
      if (hideBaseprice === 'true') {
        this.basepriceHidden = true;
      } else {
        this.basepriceHidden = false;
      }
      this.cdr.detectChanges();
    });

    this.resultsSubscription = this.results$.pipe(debounceTime(1000)).subscribe((searchResults) => {
      if (searchResults.products && searchResults.products.length) {
        this.gtmSearchBoxProductImpressions(searchResults.products);
      }
    });
    this.businessType = this.currentBusinesstype.getBusinessType();
    this.tabletCustomSylings();
  }

  @HostListener('window:resize')
  onResize(): void {
    setTimeout(() => {
      this.tabletCustomSylings();
      this.cdr.detectChanges();
    }, 500);
  }

  tabletCustomSylings() {
    const elSearchBox = document.querySelector('.SearchBox');
    if (this.deviceStylings.isTabletMaxWidthExceeds() && !this.isSearchboxFocused()) {
      elSearchBox?.classList?.add('adjustedStylings');
      this.adjustedTabletStylings = true;
    } else if (!this.deviceStylings.isTabletMaxWidthExceeds()) {
      elSearchBox?.classList.remove('adjustedStylings');
      this.adjustedTabletStylings = false;
    }
  }
  /**
   * Returns the backend configuration or default configuration for the searchbox.
   */
  private get config$(): Observable<SearchBoxConfig> {
    if (this.componentData) {
      return this.componentData.data$.pipe(
        // Since the backend returns string values (i.e. displayProducts: "true") for
        // boolean values, we replace them with boolean values.
        map((c) => {
          return {
            ...c,
            displayProducts: (c.displayProducts as any) === 'true' || c.displayProducts === true,
            displayProductImages: (c.displayProductImages as any) === 'true' || c.displayProductImages === true,
            displaySuggestions: (c.displaySuggestions as any) === 'true' || c.displaySuggestions === true,
          };
        })
      ) as Observable<SearchBoxConfig>;
    } else {
      return of(DEFAULT_SEARCHBOX_CONFIG);
    }
  }

  /**
   * Closes the searchbox and opens the search result page.
   */
  search(query: string): void {
    this.searchBoxComponentService.search(query, this.config);
    // force the searchbox to open
    this.open();
  }

  /**
   * Opens the typeahead searchbox
   */
  open(): void {
    this.adjustedTabletStylings = false;
    this.searchBoxComponentService.toggleBodyClass('searchbox-is-active', true);
    setTimeout(() => {
      this.searchInput.nativeElement.focus(); // align the cursor properly
    }, 0);
  }

  /**
   * Closes the typehead searchbox.
   */
  close(event: UIEvent, force?: boolean): void {
    // Use timeout to detect changes
    setTimeout(() => {
      if ((!this.ignoreCloseEvent && !this.isSearchboxFocused()) || force) {
        this.blurSearchBox(event);
      }
    });
  }

  protected blurSearchBox(event: UIEvent): void {
    this.searchBoxComponentService.toggleBodyClass('searchbox-is-active', false);
    if (event && event.target) {
      (event.target as HTMLElement).blur();
    }
  }

  // Check if focus is on searchbox or result list elements
  private isSearchboxFocused(): boolean {
    return (
      this.getResultElements().includes(this.getFocusedElement()) ||
      this.winRef.document.querySelector('input[aria-label="search"]') === this.getFocusedElement()
    );
  }

  /**
   * Especially in mobile we do not want the search icon
   * to focus the input again when it's already open.
   */
  avoidReopen(event: UIEvent): void {
    if (this.searchBoxComponentService.hasBodyClass('searchbox-is-active')) {
      this.close(event);
      event.preventDefault();
    }
  }

  // Return result list as HTMLElement array
  private getResultElements(): HTMLElement[] {
    return Array.from(this.winRef.document.querySelectorAll('.products > a, .suggestions > a'));
  }

  // Return focused element as HTMLElement
  private getFocusedElement(): HTMLElement {
    return this.winRef.document.activeElement as HTMLElement;
  }

  private getFocusedIndex(): number {
    return this.getResultElements().indexOf(this.getFocusedElement());
  }

  // Focus on previous item in results list
  focusPreviousChild(event: any) {
    event.preventDefault(); // Negate normal keyscroll
    const [results, focusedIndex] = [this.getResultElements(), this.getFocusedIndex()];
    // Focus on last index moving to first
    if (results.length) {
      if (focusedIndex < 1) {
        results[results.length - 1].focus();
      } else {
        results[focusedIndex - 1].focus();
      }
    }
  }

  // Focus on next item in results list
  focusNextChild(event: any) {
    event.preventDefault(); // Negate normal keyscroll
    const [results, focusedIndex] = [this.getResultElements(), this.getFocusedIndex()];
    // Focus on first index moving to last
    if (results.length) {
      if (focusedIndex >= results.length - 1) {
        results[0].focus();
      } else {
        results[focusedIndex + 1].focus();
      }
    }
  }

  /**
   * Opens the PLP with the given query.
   *
   * TODO: if there's a single product match, we could open the PDP.
   */
  launchSearchResult(event: UIEvent, query: string): void {
    if (!query || query.trim().length === 0) {
      return;
    }
    this.close(event);
    const click$ = fromEvent(document, 'click');
    this.runSearch = true;
    this.resultsSubscription = combineLatest([this.results$, this.searchService.getResults()])
      .pipe(
        auditTime(500),
        takeUntil(click$),
        map(([results, data]) => {
          if (this.runSearch) {
            if (query.trim().length >= this.config.minCharactersBeforeRequest) {
              if (typeof results.products === 'undefined') {
                this.gtmSearch(query, '/search-empty');
                this.searchBoxComponentService.search(query, this.config);
                throw results;
              } else {
                this.runSearch = false;
                this.componentService.setSearchBoxSuggestions(results.suggestions);
                if (results.products && results.products.length > 0) {
                  this.keywordRedirectUrl(data, query, true);
                } else {
                  this.keywordRedirectUrl(data, query, false);
                }
              }
            } else {
              this.runSearch = false;
              this.keywordRedirectUrl(data, query, false);
            }
          }
        }),
        retryWhen((errors) =>
          errors.pipe(
            tap(),
            delayWhen(() => timer(2000))
          )
        )
      )
      .subscribe();
  }

  keywordRedirectUrl(data: any, query: any, hasProduct: any) {
    if (data.keywordRedirectUrl && data.freeTextSearch === query) {
      if (data.keywordRedirectUrl.indexOf('/') === 0) {
        this.gtmSearch(query, data.keywordRedirectUrl);
        this.router.navigateByUrl(data.keywordRedirectUrl);
      } else {
        this.gtmSearch(query, data.keywordRedirectUrl);
        window.open(data.keywordRedirectUrl, '_blank');
      }
    } else {
      if (hasProduct) {
        this.gtmSearch(query, '/search');
        this.customLaunchSearchPage(query);
      } else {
        this.gtmSearch(query, '/search-empty');
        this.router.navigateByUrl('/search-empty');
      }
    }
  }

  checkKeywordRedirectUrl(query: any, hasProduct: any) {
    this.searchService
      .getResults()
      .pipe(take(1))
      .subscribe((data) => {
        this.componentService.setSearchBoxSuggestions([]);
        if (data.keywordRedirectUrl && data.freeTextSearch === query) {
          if (data.keywordRedirectUrl.indexOf('/') === 0) {
            this.gtmSearch(query, data.keywordRedirectUrl);
            this.router.navigateByUrl(data.keywordRedirectUrl);
          } else {
            this.gtmSearch(query, data.keywordRedirectUrl);
            window.open(data.keywordRedirectUrl, '_blank');
          }
        } else {
          if (hasProduct) {
            this.gtmSearch(data.freeTextSearch, '/search');
            this.customLaunchSearchPage(query);
          } else {
            this.gtmSearch(query, '/search-empty');
            this.router.navigateByUrl('/search-empty');
          }
        }
      });
  }

  updateSearchInput(suggestion: string) {
    this.searchInput.nativeElement.value = suggestion;
  }

  gtmSearch(query: any, destination: any) {
    let gtmSearch = {
      event: GoogleTagManagerEvent.SEARCH,
      ecommerce: '',
      search_term: query,
      results_page: destination,
      affiliation: this.affiliation,
    };
    if (query) {
      this.gtmService.pushTag({
        ecommerce: null,
        search_term: '',
        results_page: '',
        affiliation: '',
        content_type: '',
        content_id: '',
        content_name: '',
      });
      this.gtmService.pushTag(gtmSearch);
    }
  }

  /**
   * Disables closing the search result list.
   */
  disableClose(): void {
    this.ignoreCloseEvent = true;
  }

  /**
   * Clears the search box input field
   */
  public clear(el: HTMLInputElement): void {
    this.disableClose();
    el.value = '';
    this.searchBoxComponentService.clearResults();
  }

  closeSearchBox() {
    this.searchBoxComponentService.toggleBodyClass('searchbox-is-active', false);
  }

  getImageRendition(image: any) {
    let size: string;
    size = ShimanoUtilMethod.imageRendition(80);
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  goToPdp(product: Product, position: number, query: any) {
    this.gtmSearch(query, product.url);
    this.gtmSearchBoxProductClick(product);

    this.routingService.go({
      cxRoute: 'product',
      params: product,
    });
  }

  get isMobile(): boolean {
    return window.innerWidth < this.cxMobileWidth;
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  gtmSearchBoxProductImpressions(products: any[]) {
    const impressionsList: any[] = [];
    products.forEach((product, index) => {
      impressionsList.push({
        item_id: product.baseProduct,
        item_name: product.baseProductName,
        affiliation: this.affiliation,
        index: index,
        item_brand: '',
        price: this.showSrp ? product.srpPrice.formattedValue : product.basePrice.formattedValue,
        item_list_name: GoogleTagManagerProductListName.SEARCH_BOX,
      });
    });
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_IMPRESSION,
      ecommerce: {
        currency: this.storageService.getSessionStorageItem('currency'),
        items: impressionsList,
      },
    };
    this.gtmService.pushTag({
      ecommerce: null,
      search_term: '',
      results_page: '',
      affiliation: '',
      content_type: '',
      content_id: '',
      content_name: '',
    });
    this.gtmService.pushTag(gtmTag);
  }

  gtmSearchBoxProductClick(product: any) {
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_CLICK,
      ecommerce: '',
      content_type: 'product',
      content_id: product.code,
      content_name: product.name,
    };
    this.gtmService.pushTag({
      ecommerce: null,
      search_term: '',
      results_page: '',
      affiliation: '',
      content_type: '',
      content_id: '',
      content_name: '',
    });
    this.gtmService.pushTag(gtmTag);
  }

  customLaunchSearchPage(query: string) {
    this.routingService.go({
      cxRoute: 'search',
      params: { query },
    });
  }

  ngOnDestroy() {
    if (this.resultsSubscription) {
      this.resultsSubscription.unsubscribe();
    }
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.basepriceBehaviorConfig$) {
      this.basepriceBehaviorConfig$.unsubscribe();
    }
  }
}
